import { useEffect, useMemo, useState } from "react";
import { PAGES } from "../routes/PAGES";
import { useCommunity } from "context/community";
import LangService from "./LangService";

const documents = {
    "RU": {
        showAgreement: true,
        agreementUrl: PAGES.AGREEMENT_RU,
        policyUrl: PAGES.PRIVATE_POLICY,
        termsUrl: PAGES.TERMS_OF_USE
    },
    "EN": {
        showAgreement: false,
        agreementUrl: null,
        policyUrl: PAGES.PRIVATE_POLICY_EN,
        termsUrl: PAGES.TERMS_OF_USE_EN
    },
    "JO": {
        showAgreement: false,
        agreementUrl: null,
        policyUrl: PAGES.PRIVATE_POLICY_JO,
        termsUrl: PAGES.TERMS_OF_USE_JO
    },
    "TIM": {
        showAgreement: false,
        agreementUrl: null,
        policyUrl: PAGES.PRIVATE_POLICY_JO,
        termsUrl: PAGES.TERMS_OF_USE_JO
    }
}

const POLICY_CONFIG_OPTION_TYPE = "POLICY_CONFIG";
const DEFAULT_POLICY_CONFIG = {
    policyLocale: "GEO",
    preLoginConsentRequired: true
};
const CONSENT_TEXT_CONFIG_KEYS = {
    enabled: "customConsentTextEnabled",
    value: "customConsentText"
};
const PRE_LOGIN_CONSENTS_CONFIG_KEY = "preLoginConsents";
const DEFAULT_CONSENT_ID_PREFIX = "pre-login-consent";
const DEFAULT_CONSENT_LOCALE = "ru";
const FALLBACK_CONSENT_LOCALE = "en";

const getEnv = () => {
    return process.env.REACT_APP_SERVER_ENV;
}

const extractOptionType = (option) => {
    if (typeof option === "string") {
        return option;
    }
    if (option && typeof option === "object") {
        return option.optionType || option.type || "";
    }
    return "";
}

const getPolicyConfigFromOptions = (options) => {
    if (!Array.isArray(options) || !options.length) {
        return DEFAULT_POLICY_CONFIG;
    }

    const rawOption = options.find((option) => {
        const optionType = extractOptionType(option);
        return optionType === POLICY_CONFIG_OPTION_TYPE;
    });

    if (!rawOption || typeof rawOption === "string") {
        return DEFAULT_POLICY_CONFIG;
    }

    // API returns optionValue; support both for compatibility
    const value = rawOption.value ?? rawOption.optionValue;

    if (!value || typeof value !== "string") {
        return DEFAULT_POLICY_CONFIG;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        console.warn("Failed to parse POLICY_CONFIG option value", error);
        return DEFAULT_POLICY_CONFIG;
    }
}

const resolveLocale = ({ policyLocale, code, env }) => {
    // New configurable behaviour:
    // - "RU": always use Russian documents
    // - "EN": map to JO documents (per current product requirement)
    // - "GEO": use geo-IP based code ("RU" for RU/BY, else "JO")
    if (policyLocale === "RU") {
        return "RU";
    }
    if (policyLocale === "EN") {
        return "JO";
    }
    if (policyLocale === "GEO" && code) {
        return code;
    }

    // Fallbacks: existing behaviour based on geo code, then env, then EN
    if (code && documents[code]) {
        return code;
    }
    if (env && documents[env]) {
        return env;
    }
    return "EN";
}

const VOLEE_SITE = 'https://volee.space';

const isVoleeBrand = (brandConfig) => brandConfig?.siteUrl === VOLEE_SITE;

const getDocsConfig = ({ code, env, policyConfig, brandConfig }) => {
    const policyLocale = isVoleeBrand(brandConfig) ? 'RU' : policyConfig?.policyLocale;
    const localeKey = resolveLocale({ policyLocale, code, env });
    const base = documents[localeKey] || documents["EN"];

    if (!policyConfig?.overrideUrls) {
        return base;
    }

    const overrideUrls = policyConfig.overrideUrls;

    return {
        ...base,
        agreementUrl: overrideUrls.agreement || base.agreementUrl,
        policyUrl: overrideUrls.policy || base.policyUrl,
        termsUrl: overrideUrls.terms || base.termsUrl
    };
}

const getLocalizedConsentText = (customText, locale) => {
    if (typeof customText === "string") {
        return customText.trim();
    }

    if (!customText || typeof customText !== "object") {
        return "";
    }

    const normalizedLocale = (locale || "").toLowerCase();
    const localized =
        customText[normalizedLocale] ??
        customText[DEFAULT_CONSENT_LOCALE] ??
        customText[FALLBACK_CONSENT_LOCALE];

    return typeof localized === "string" ? localized.trim() : "";
}

const getConsentConfig = ({ policyConfig, locale }) => {
    const enabled = Boolean(policyConfig?.[CONSENT_TEXT_CONFIG_KEYS.enabled]);
    const customText = policyConfig?.[CONSENT_TEXT_CONFIG_KEYS.value];
    const resolvedText = getLocalizedConsentText(customText, locale);

    if (!enabled || !resolvedText) {
        return {
            enabled: false,
            text: ""
        };
    }

    return {
        enabled: true,
        text: resolvedText
    };
}

const getPreLoginConsentsConfig = ({ policyConfig, locale }) => {
    const rawConsents = policyConfig?.[PRE_LOGIN_CONSENTS_CONFIG_KEY];

    if (!Array.isArray(rawConsents) || !rawConsents.length) {
        return [];
    }

    return rawConsents
        .map((consent, index) => {
            if (!consent || typeof consent !== "object") {
                return null;
            }

            const text = getLocalizedConsentText(consent.text, locale);
            if (!text) {
                return null;
            }

            return {
                id: consent.id || `${DEFAULT_CONSENT_ID_PREFIX}-${index + 1}`,
                required: consent.required !== false,
                text
            };
        })
        .filter(Boolean);
}

const HACK_PHYSTECH4EVER = ['phystech4ever', 'brnetwork']

export { getDocsConfig };

export const useServerProperties = () => {
    const [code, setCode] = useState();
    const env = useMemo(getEnv, []);
    const { community: { domain, optionsRaw, brandConfig } } = useCommunity();
    const policyConfig = useMemo(
        () => getPolicyConfigFromOptions(optionsRaw || []),
        [optionsRaw]
    );
    const docs = useMemo(
        () => getDocsConfig({ code, env, policyConfig, brandConfig }),
        [code, env, policyConfig, brandConfig]
    );
    const consentConfig = useMemo(
        () => getConsentConfig({
            policyConfig,
            locale: (LangService.lang || FALLBACK_CONSENT_LOCALE).toLowerCase()
        }),
        [policyConfig, LangService.lang]
    );
    const preLoginConsents = useMemo(
        () => getPreLoginConsentsConfig({
            policyConfig,
            locale: (LangService.lang || FALLBACK_CONSENT_LOCALE).toLowerCase()
        }),
        [policyConfig, LangService.lang]
    );

    useEffect(() => {
        const fetchCountryCode = async () => {
            return await fetch('https://get.geojs.io/v1/ip/country.json')
                .then((data) => data.json())
                .then((data) => data.country)
                .then((countryCode) => {
                    let resolvedCode = countryCode;
                    switch (countryCode) {
                        case "RU":
                        case "BY":
                            resolvedCode = "RU";
                            break;
                        default:
                            resolvedCode = "JO";
                            break;
                    }
                    setCode(resolvedCode)
                })
                .catch(console.warn)
        }

        const needsGeo =
            HACK_PHYSTECH4EVER.includes(domain) ||
            policyConfig?.policyLocale === "GEO";

        if (needsGeo) {
            fetchCountryCode()
        }
    }, [domain, policyConfig])

    return {
        env,
        docs,
        policyConfig,
        consentConfig,
        preLoginConsents
    }
}


